/**
 * app-home.js
 */

import React from 'react';
import { 
  Container, 
  Box,
  Typography,
  Button,
} from '@mui/material';
import { Link } from "react-router-dom";
import { UserContext } from 'contexts/user/user-context';
import { AppUtilityContext } from 'contexts/app-utility/app-utility-context';
import { AppMessagingContext } from 'contexts/app-messaging/app-messaging-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import { LiveTrackingContext } from 'components/tracking/live-tracking/live-tracking-context';
import watermarkLogo from 'assets/images/pt1-watermark.png';
import footer from 'assets/images/pt1-footer.png';
import trainingButton from 'assets/images/pt1-training.png';
import deploymentButton from 'assets/images/pt1-deployment.png';
import groupsButton from 'assets/images/pt1-groups.png';
import trackingButton from 'assets/images/pt1-tracking.png';
import { renderForMobileContext } from 'utils/app-context';


const MainAction = (props) => {
  const { img, to, alt } = props;


  return (
    <Box sx={{ 
      px: 1.5,
      '& img': {
        maxWidth: '100%',
        height: 'auto'
      } 
    }}>
      <Link to={to}>
        <img src={img} alt={alt} />
      </Link>
    </Box>
  );
}



export default function AppHome() {
  const { user } = React.useContext(UserContext);
  const { alertWarning } = React.useContext(AppUtilityContext);
  const { setAppBar } = React.useContext(AppBarContext);
  const { appInfo } = React.useContext(AppMessagingContext);
  const { setIsTrackingOpen } = React.useContext(LiveTrackingContext);
  const canTrack = (renderForMobileContext() && !window.ReactNativeWebView)  // testing mobile UI outside app
    || (appInfo?.appVersion ?? '' ) > '4.6.1';                               // app version sufficient


  // reset app bar
  React.useEffect(() => {
    setAppBar('', []);
  }, [setAppBar]);


  // show warning if app version doesn't support tracking
  React.useEffect(() => {
    if (!canTrack) {
      alertWarning({
        title: 'Map Tracking Disabled',
        text: `We recently upgraded PACKTRACK's location tracking system. Your current app is out of date and tracking is disabled. If you don't need map tracking functionality then you can continue to use this older app version for a while longer. However, if you need map tracking then you should update this app right away in your app store.`
      });
    }
  }, [canTrack, alertWarning]);


  return (
    <Container sx={{
      backgroundColor: '#003e6b',
      height: '100%',
      p: 0
    }}>
      <Box sx={{
        height: '100%',
        width: '100%',
        background: `url(${watermarkLogo}) no-repeat top center;`,
        backgroundSize: 'contain',
        pt: '5vh',
      }}>
        <Box sx={{
          height: '100%',
          width: '100%',
          px: 2,
          position: 'relative',
          zIndex: 1,
          '&:before': {
            content: `""`,
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            background: `url(${footer}) repeat-x bottom center`,
            opacity: 0.35,
            zIndex: -1,
          }
        }}>
          <Typography variant='caption' sx={{
            display: 'block',
            color: 'white',
            textAlign: 'center',
            letterSpacing: 3,
          }}>
            WELCOME BACK
          </Typography>
          <Link to={'/profile'} style={{ textDecoration: 'none' }}>
            <Typography variant='h6' sx={{
              color: 'white',
              textAlign: 'center',
              fontSize: 'sizes.6',
            }}>
              {user.name}
            </Typography>
          </Link>

          <Box sx={{
            mt: '4vh',
            display: 'flex',
            justifyContent: 'space-evenly'
          }}>
            <MainAction img={trainingButton} to='/records' alt='training records' />
            <MainAction img={deploymentButton} to='/records/deployment-drawer/0/0/edit/0' alt='log deployment' />
            <MainAction img={groupsButton} to='/groups' alt='groups' />
          </Box>

          <Box sx={{
            mt: '4vh',
            display: 'flex',
            justifyContent: 'center',
            '& button': {
              width: '35vw',
              textAlign: 'center',
              padding: 0,
              '& img': {
                maxWidth: '100%'  
              }
            }
          }}>
            <Button 
              onClick={() => canTrack ? setIsTrackingOpen(true) : {}}
              style={canTrack
                ? {}
                : { 
                  pointerEvents: 'none',
                  cursor: 'default',
                  opacity: 0.7,
                  filter: 'brightness(0.7)',
                }}
            >
              <img src={trackingButton} alt='tracking' />
            </Button>
          </Box>

          <Box sx={{
            mt: '5vh',
            color: 'yellow',
            textAlign: 'center',
          }}>
            <Typography>Login with a 4-digit number!</Typography>
          </Box>

          <Box sx={{
            mt: '3vh',
            color: 'white',
            textAlign: 'center',
            px: 3,
          }}>
            <Typography sx={{ lineHeight: '3vh' }}>
              Instead of an email and password you can login to this app with a simple passcode. 
              Go to the Profile screen and select the Quick Access Passcode option.
              Note that you'll need to update this app to the latest version from your App Store.
            </Typography>
          </Box>

          <Box sx={{
            position: 'absolute',
            bottom: 40,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-between',
            color: 'white',
            px: 7,
            '& span': {
              display: 'flex'
            },
            '& a': {
              color: 'white',
              textDecoration: 'none',
            }
          }}>
            <Link to='/privacy-policy'><Typography variant='caption'>PRIVACY POLICY</Typography></Link>
            <Typography variant='caption'>VERSION: {appInfo?.appVersion ?? 'n/a'}</Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  )
}